//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

// #region Imports
import { EthAddr, TxHash } from '@shared/SchemaTypes'
import { Contract, Signer } from 'ethers'

import { ActivityEnum, RoleEnum } from '@shared/Enums'

import { poolActions } from '@react/redux/poolSlice'

import { logOT } from '@shared/loggingShared'

import { ThunkAction, UnknownAction } from '@reduxjs/toolkit'

import { assertRole, dispatchError, OtStoreState } from '@react/redux/store'

import { PoolDynamicAbi } from '@shared/Abis'
import { DynamicWithdrawalActivityCTO } from '@shared/PoolEventCTO'
import { txUUID, wsServerActionPoolTX, wsServerActionPoolTXRequest } from '@shared/WsServerActions'
import { PoolDynamic } from '@typechain-types/contracts'
import { otAssert } from '@shared/conversions'

export const changeWithdrawDestinationTx =
  (props: {
    activity: DynamicWithdrawalActivityCTO
    destination: EthAddr
  }): ThunkAction<void, OtStoreState, unknown, UnknownAction> =>
  async (dispatch, getState): Promise<void> => {
    const activity = ActivityEnum.WITHDRAW_REQUEST

    try {
      const { webSocket, userConnection } = getState().chainState
      const { providerReadOnly } = getState().chainState.chainConfiguration
      otAssert(providerReadOnly, 'providerReadOnly is not defined')
      const { chainConfigurationName } = getState().chainState.chainConfiguration
      otAssert(userConnection, 'userConnection is not defined')
      const { roleEnum, signer } = userConnection
      assertRole(roleEnum, RoleEnum.POOL_ADMIN)
      const { activity, destination } = props

      logOT.debug('repayEarlyWithdraw', activity.poolAddr)
      await changeWithdrawDestination(webSocket, signer as Signer, activity, destination, chainConfigurationName)
      dispatch(poolActions.changeWithdrawDestinationSuccess(props))
    } catch (e: any) {
      dispatch(poolActions.changeWithdrawDestinationFail(props))
      console.error('repayEarlyWithdraw error', e)
      await dispatchError(dispatch, e, activity, 1017)
    }
  }

export const changeWithdrawDestination = async (
  webSocket: WebSocket | undefined,
  adminSigner: Signer,
  activity: DynamicWithdrawalActivityCTO,
  destination: EthAddr,
  chainConfigurationName: string
): Promise<void> => {
  const { poolAddr, uuid, lender } = activity

  const txUuid = txUUID()
  webSocket?.send(wsServerActionPoolTXRequest(ActivityEnum.WITHDRAW_REQUEST, poolAddr, txUuid, chainConfigurationName))
  const poolContract = new Contract(poolAddr, PoolDynamicAbi, adminSigner) as unknown as PoolDynamic

  const tx = await poolContract.changeRedemptionDestination(lender, uuid, destination)

  webSocket?.send(
    wsServerActionPoolTX(ActivityEnum.WITHDRAW_REQUEST, poolAddr, txUuid, tx.hash as TxHash, chainConfigurationName)
  )
  await tx.wait(1)

  logOT.debug('repayEarlyWithdraw tx', tx)
}
